/**
 * Renders a `wizard_preview` view: runs the real embeddable-wizard question
 * endpoint, through the generic `agentViewData(component: "wizard_preview")`
 * query (MEIS-13516 P2; replaces the former `POST /api/agent/wizard-preview`
 * route), for one journey, lets the user switch journeys, answer questions
 * and see the visible questions update live, reset the answers, and show
 * hidden questions on request.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useApolloClient } from "@apollo/client";
import { getDataOrThrow } from "@msys/common";
import { useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import * as Sentry from "@sentry/react";
import React from "react";
import type { AgentViewComponentProps } from "../registry.js";
import { TemplateViewFrame } from "./TemplateViewFrame.js";
import {
  wizardPreviewResultSchema,
  wizardPreviewViewSchema,
  type AskWhen,
  type AskWhom,
  type WizardAnswer,
  type WizardDecision,
  type WizardPreviewResult,
  type WizardPreviewView as WizardPreviewViewPayload,
} from "./templateViewTypes.js";
import { WizardPreviewDataDocument } from "./WizardPreviewView.generated.js";
import {
  useWizardPreviewState,
  wizardPreviewKey,
  type WizardPreviewState,
} from "./wizardPreviewStore.js";

/** Pause after the last keystroke before a typed answer re-runs the preview query. */
const TYPED_ANSWER_DEBOUNCE_MS = 300;

type Journey = { askWhen: AskWhen; askWhom: AskWhom };

function useJourneys(): Array<Journey & { label: string }> {
  const { t } = useTranslate(["Assistant", "Global"]);
  return [
    {
      askWhen: "onEmbeddableWizard",
      askWhom: "contractee",
      label: t("Online (end customer)", { ns: "Assistant" }),
    },
    {
      askWhen: "onCallAssistant",
      askWhom: "contractor",
      label: t("Phone", { ns: "Global" }),
    },
    {
      askWhen: "onQuoteCreate",
      askWhom: "contractor",
      label: t("On-site", { ns: "Assistant" }),
    },
  ];
}

function sameJourney(a: Journey, b: Journey) {
  return a.askWhen === b.askWhen && a.askWhom === b.askWhom;
}

function WizardPreviewViewInner({ view }: { view: WizardPreviewViewPayload }) {
  const { t } = useTranslate(["Assistant"]);
  const { getFormattedPrice } = useFormatting();
  const apolloClient = useApolloClient();
  const journeys = useJourneys();

  // Journey, answers and decisions live in a module-scope store keyed by the
  // spec's content, so they survive the chat remounting this view. Identical
  // specs share their state (the card and its enlarged dialog by design).
  const storeKey = wizardPreviewKey(view);
  const initialState = React.useMemo<WizardPreviewState>(
    () => ({
      journey: { askWhen: view.askWhen, askWhom: view.askWhom },
      answers: view.answers,
      decisions: view.decisions,
    }),
    // `storeKey` captures every field read here; `view` is re-parsed per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storeKey]
  );
  const [{ journey, answers, decisions }, updateStored] = useWizardPreviewState(
    storeKey,
    initialState
  );
  const setAnswers = (change: (list: WizardAnswer[]) => WizardAnswer[]) =>
    updateStored(s => ({ ...s, answers: change(s.answers) }));
  const setDecisions = (change: (list: WizardDecision[]) => WizardDecision[]) =>
    updateStored(s => ({ ...s, decisions: change(s.decisions) }));
  // The free-text/number `TextField`'s own raw, uncommitted input per key,
  // kept alongside the committed `answers` (task 12.3): while a key has an
  // entry here, the field displays it verbatim instead of the committed/
  // stored value, so e.g. a `Number` field showing "12" that the user is
  // editing into "12.5" does not snap back to "12" the instant "12." makes
  // the input momentarily empty (see the `TextField` below). Cleared once
  // the field blurs or "Reset answers" is pressed.
  const [draftByKey, setDraftByKey] = React.useState<Record<string, string>>(
    {}
  );
  // Pending debounced commits of typed text/number answers, per key. Typing
  // updates the answer (and so re-runs the loopback query) only once the user
  // pauses; blur/Enter commit immediately, reset/unmount cancel.
  const typedCommitTimers = React.useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );
  const cancelTypedCommit = (key: string) => {
    const timer = typedCommitTimers.current.get(key);
    if (timer !== undefined) clearTimeout(timer);
    typedCommitTimers.current.delete(key);
  };
  const cancelAllTypedCommits = () => {
    typedCommitTimers.current.forEach(timer => clearTimeout(timer));
    typedCommitTimers.current.clear();
  };
  React.useEffect(() => cancelAllTypedCommits, []);
  const [showHidden, setShowHidden] = React.useState(false);
  const [state, setState] = React.useState<{
    result: WizardPreviewResult | null;
    loading: boolean;
    error: boolean;
  }>({ result: null, loading: true, error: false });

  const requestBody = React.useMemo(
    () => ({
      templateId: view.templateId,
      unpublished: view.unpublished,
      answers,
      decisions,
      askWhen: journey.askWhen,
      askWhom: journey.askWhom,
    }),
    [
      view.templateId,
      view.unpublished,
      answers,
      decisions,
      journey.askWhen,
      journey.askWhom,
    ]
  );
  const requestKey = JSON.stringify(requestBody);

  React.useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: false }));
    apolloClient
      .query({
        query: WizardPreviewDataDocument,
        variables: { params: JSON.parse(requestKey) as typeof requestBody },
        fetchPolicy: "network-only",
        // This view shows its own inline error (below); the global Apollo
        // error link must not also toast a GraphQL error for it.
        context: { noErrorNotification: true },
      })
      .then(({ data }) => {
        if (cancelled) return;
        const viewData = getDataOrThrow(data.agentViewData);
        const parsed = wizardPreviewResultSchema.safeParse(viewData.data);
        if (!parsed.success) {
          throw new Error(
            `Invalid wizard preview data: ${parsed.error.message}`
          );
        }
        setState({ result: parsed.data, loading: false, error: false });
      })
      .catch(err => {
        Sentry.captureException(err);
        if (!cancelled) setState({ result: null, loading: false, error: true });
      });
    return () => {
      cancelled = true;
    };
    // `requestKey` alone decides when to re-fetch; `apolloClient` is excluded
    // (mirroring `useAgentAccess.ts`) since a mocked `useApolloClient` in
    // tests returns a fresh object per render, which would otherwise re-run
    // this on every render instead of only when the request actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  const propValueByKey = React.useMemo(() => {
    const map = new Map<
      string,
      string | number | boolean | Array<string | number> | null
    >();
    for (const q of state.result?.questions ?? []) {
      if (q.type !== "properties") continue;
      for (const p of q.props) map.set(p.key, p.value);
    }
    return map;
  }, [state.result]);
  const answerOf = (key: string) => {
    const local = answers.find(a => a.key === key)?.value;
    if (local !== undefined) return local;
    // While a request for the current `answers`/`decisions` is in flight
    // (e.g. right after "Reset answers"), `state.result` is still the
    // PREVIOUS response and its prop values would resurrect the just-reset
    // answer for one render. Only trust the stored value once the result
    // that matches the current inputs has actually come back.
    if (state.loading) return undefined;
    return propValueByKey.get(key) ?? undefined;
  };
  const setAnswer = (
    key: string,
    value: string | number | boolean | Array<string | number> | null
  ) =>
    setAnswers(list => [
      ...list.filter(a => a.key !== key),
      ...(value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
        ? []
        : [{ key, value }]),
    ]);

  const decisionOf = (itemId: string) =>
    decisions.find(d => d.itemId === itemId)?.selected ?? [];
  const setDecisionSelection = (
    itemId: string,
    questionId: string,
    selected: string[]
  ) =>
    setDecisions(list => [
      ...list.filter(d => d.itemId !== itemId),
      ...(selected.length > 0 ? [{ itemId, questionId, selected }] : []),
    ]);

  const resetAnswers = () => {
    cancelAllTypedCommits();
    updateStored(s => ({ ...s, answers: [], decisions: [] }));
    setDraftByKey({});
  };

  const questions = state.result?.questions ?? [];
  const hiddenCount = questions.reduce(
    (n, q) =>
      n +
      (q.type === "properties" ? q.props.filter(p => !p.visible).length : 0),
    0
  );

  return (
    <TemplateViewFrame
      title={view.title}
      description={view.description}
      renderBody={compact => (
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ flexWrap: "wrap", rowGap: 0.5, alignItems: "center" }}
          >
            {journeys.map(j => (
              <Chip
                key={`${j.askWhen}/${j.askWhom}`}
                size="small"
                label={j.label}
                color={sameJourney(j, journey) ? "primary" : "default"}
                onClick={() => {
                  if (sameJourney(j, journey)) return;
                  // The old journey's answers/decisions would go into the new
                  // journey's request, which rejects keys it does not ask.
                  cancelAllTypedCommits();
                  setDraftByKey({});
                  updateStored(() => ({
                    journey: { askWhen: j.askWhen, askWhom: j.askWhom },
                    answers: [],
                    decisions: [],
                  }));
                }}
              />
            ))}
            {(answers.length > 0 || decisions.length > 0) && (
              <Button size="small" onClick={resetAnswers}>
                {t("Reset answers", { ns: "Assistant" })}
              </Button>
            )}
            {state.loading && <CircularProgress size={14} />}
          </Stack>

          {state.error && (
            <Alert severity="error">
              {t("The preview could not be loaded.", { ns: "Assistant" })}
            </Alert>
          )}
          {state.result?.notPublished && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {t(
                "Not published yet: end customers do not see this journey yet.",
                {
                  ns: "Assistant",
                }
              )}
            </Alert>
          )}
          {state.result && questions.length === 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {t("This journey asks no questions.", { ns: "Assistant" })}
            </Alert>
          )}

          {(compact ? questions.slice(0, 4) : questions).map((q, index) => (
            <Paper
              key={`${q.itemId}-${index}`}
              variant="outlined"
              sx={{ p: 1 }}
            >
              <Typography variant="caption" color="text.secondary">
                {q.itemTitle}
              </Typography>
              {q.type === "properties" &&
                q.props
                  .filter(p => p.visible || showHidden)
                  .map(p => (
                    <Box
                      key={p.key}
                      sx={{ mt: 0.5, opacity: p.visible ? 1 : 0.45 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {p.label}
                        {p.unit ? ` (${p.unit})` : ""}
                        {!p.visible && ` – ${t("hidden", { ns: "Assistant" })}`}
                      </Typography>
                      {p.options.length > 0 &&
                      (p.kind === "Text" || p.kind === "Number") ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.25 }}
                        >
                          {p.options.map(option => (
                            <Chip
                              key={String(option)}
                              size="small"
                              label={String(option)}
                              aria-label={`${p.label}: ${String(option)}`}
                              disabled={!p.visible}
                              color={
                                answerOf(p.key) === option
                                  ? "primary"
                                  : "default"
                              }
                              onClick={() =>
                                setAnswer(
                                  p.key,
                                  answerOf(p.key) === option ? null : option
                                )
                              }
                            />
                          ))}
                        </Stack>
                      ) : p.kind === "TextArray" || p.kind === "NumberArray" ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.25 }}
                        >
                          {p.options.map(option => {
                            const selected = answerOf(p.key);
                            const selectedList = Array.isArray(selected)
                              ? selected
                              : [];
                            const isSelected = selectedList.includes(option);
                            return (
                              <Chip
                                key={String(option)}
                                size="small"
                                label={String(option)}
                                aria-label={`${p.label}: ${String(option)}`}
                                disabled={!p.visible}
                                color={isSelected ? "primary" : "default"}
                                onClick={() =>
                                  setAnswer(
                                    p.key,
                                    isSelected
                                      ? selectedList.filter(v => v !== option)
                                      : [...selectedList, option]
                                  )
                                }
                              />
                            );
                          })}
                        </Stack>
                      ) : p.kind === "Bool" ? (
                        <Switch
                          size="small"
                          disabled={!p.visible}
                          checked={answerOf(p.key) === true}
                          slotProps={{ input: { "aria-label": p.label } }}
                          onChange={event =>
                            setAnswer(p.key, event.target.checked)
                          }
                        />
                      ) : p.kind === "Text" || p.kind === "Number" ? (
                        <TextField
                          size="small"
                          disabled={!p.visible}
                          type={p.kind === "Number" ? "number" : "text"}
                          slotProps={{ htmlInput: { "aria-label": p.label } }}
                          value={
                            p.key in draftByKey
                              ? draftByKey[p.key]
                              : (answerOf(p.key) ?? "")
                          }
                          onChange={event => {
                            const raw = event.target.value;
                            setDraftByKey(d => ({ ...d, [p.key]: raw }));
                            cancelTypedCommit(p.key);
                            typedCommitTimers.current.set(
                              p.key,
                              setTimeout(() => {
                                typedCommitTimers.current.delete(p.key);
                                setAnswer(
                                  p.key,
                                  p.kind === "Number" && raw !== ""
                                    ? Number(raw)
                                    : raw
                                );
                              }, TYPED_ANSWER_DEBOUNCE_MS)
                            );
                          }}
                          onKeyDown={event => {
                            if (
                              event.key !== "Enter" ||
                              !(event.target instanceof HTMLInputElement)
                            )
                              return;
                            const raw = event.target.value;
                            cancelTypedCommit(p.key);
                            setAnswer(
                              p.key,
                              p.kind === "Number" && raw !== ""
                                ? Number(raw)
                                : raw
                            );
                          }}
                          onBlur={event => {
                            const raw = event.target.value;
                            cancelTypedCommit(p.key);
                            setDraftByKey(d =>
                              Object.fromEntries(
                                Object.entries(d).filter(
                                  ([key]) => key !== p.key
                                )
                              )
                            );
                            setAnswer(
                              p.key,
                              p.kind === "Number" && raw !== ""
                                ? Number(raw)
                                : raw
                            );
                          }}
                        />
                      ) : (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.25 }}
                        >
                          {p.options.map(option => (
                            <Chip
                              key={String(option)}
                              size="small"
                              variant="outlined"
                              label={String(option)}
                            />
                          ))}
                          <Typography variant="caption" color="text.secondary">
                            {t("Multiple choice", { ns: "Assistant" })}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  ))}
              {q.type === "decision" && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {q.prompt || q.itemTitle}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.25 }}
                  >
                    {q.options.map(option => {
                      const selected = decisionOf(q.itemId);
                      const isSelected = selected.includes(option.itemId);
                      // The raw wizard behavior is "one" (single-select,
                      // replaces the selection) or "many" (toggle) — see
                      // `question-types-and-schema.ts`'s decision schema.
                      const next =
                        q.behavior === "many"
                          ? isSelected
                            ? selected.filter(id => id !== option.itemId)
                            : [...selected, option.itemId]
                          : isSelected
                            ? []
                            : [option.itemId];
                      const optionLabel =
                        option.price !== null
                          ? `${option.title} · ${getFormattedPrice(option.price)}`
                          : option.title;
                      return (
                        <Chip
                          key={option.itemId}
                          size="small"
                          label={optionLabel}
                          aria-label={`${q.prompt || q.itemTitle}: ${optionLabel}`}
                          color={isSelected ? "primary" : "default"}
                          onClick={() =>
                            setDecisionSelection(q.itemId, q.questionId, next)
                          }
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}
              {q.type === "other" && (
                <Typography variant="body2">
                  {t("Unsupported question type", { ns: "Assistant" })}
                </Typography>
              )}
            </Paper>
          ))}

          {compact && questions.length > 4 && (
            <Typography variant="caption" color="text.secondary">
              {t("… {count} more questions – enlarge to click through", {
                ns: "Assistant",
                count: questions.length - 4,
              })}
            </Typography>
          )}
          {!compact && hiddenCount > 0 && (
            <Box>
              <Button size="small" onClick={() => setShowHidden(v => !v)}>
                {showHidden
                  ? t("Hide hidden questions", { ns: "Assistant" })
                  : t("{count} hidden by conditions – show", {
                      ns: "Assistant",
                      count: hiddenCount,
                    })}
              </Button>
            </Box>
          )}
          {state.result && state.result.gatesLost.length > 0 && (
            <Alert severity="info" sx={{ py: 0 }}>
              {t(
                "Platform note: the wizard lost the condition of {items} after the answer; the preview evaluates the stored condition.",
                { ns: "Assistant", items: state.result.gatesLost.join(", ") }
              )}
            </Alert>
          )}
        </Stack>
      )}
    />
  );
}

export function WizardPreviewView({ view }: AgentViewComponentProps) {
  const parsed = wizardPreviewViewSchema.safeParse(view);
  if (!parsed.success) return null;
  return <WizardPreviewViewInner view={parsed.data} />;
}
